import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@pantry/ui';
import {
  useHouseholdMembers,
  useHouseholdInvitations,
  type HouseholdRole,
} from '@pantry/api-client';
import { UserGroupIcon, UserPlusIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { MemberCard } from './MemberCard';
import { InvitationCard } from './InvitationCard';
import { InviteMemberDialog } from './InviteMemberDialog';
import { LeaveHouseholdDialog } from './LeaveHouseholdDialog';

interface MemberListProps {
  householdId: string;
  householdName: string;
  currentUserId: string;
  currentUserRole: HouseholdRole;
}

export function MemberList({
  householdId,
  householdName,
  currentUserId,
  currentUserRole,
}: MemberListProps) {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const { data: members, isLoading: membersLoading } = useHouseholdMembers(householdId);
  const { data: invitations, isLoading: invitationsLoading } = useHouseholdInvitations(householdId);

  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin' || isOwner;
  const canInvite = isAdmin;
  const canLeave = !isOwner; // Owner must transfer ownership first

  // Filter out expired invitations for display, but keep recently expired ones
  const pendingInvitations = invitations?.filter((inv) => !inv.acceptedAt) || [];

  const isLoading = membersLoading || invitationsLoading;

  if (isLoading) {
    return (
      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <UserGroupIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Members</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-xl" data-tour="members-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <UserGroupIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Members</Trans>
            {members && (
              <span className="ml-1 text-sm font-normal text-muted-foreground">
                ({members.length})
              </span>
            )}
          </CardTitle>
          <div className="flex gap-2">
            {canLeave && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLeaveDialog(true)}
                className="rounded-xl"
              >
                <Trans>Leave</Trans>
              </Button>
            )}
            {canInvite && (
              <Button
                size="sm"
                onClick={() => setShowInviteDialog(true)}
                className="rounded-xl"
                data-tour="invite-button"
              >
                <UserPlusIcon className="h-4 w-4 mr-1.5" />
                <Trans>Invite</Trans>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Members List */}
          <div className="space-y-2">
            {members?.map((member, index) => (
              <div
                key={member.id}
                className="animate-in fade-in slide-in-from-bottom-1"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <MemberCard
                  householdId={householdId}
                  member={member}
                  currentUserId={currentUserId}
                  currentUserRole={currentUserRole}
                />
              </div>
            ))}
          </div>

          {/* Pending Invitations */}
          {pendingInvitations.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-border/50">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2 pt-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted/50">
                  <EnvelopeIcon className="h-3.5 w-3.5" />
                </div>
                <Trans>Pending Invitations ({pendingInvitations.length})</Trans>
              </h4>
              <div className="space-y-2">
                {pendingInvitations.map((invitation, index) => (
                  <div
                    key={invitation.id}
                    className="animate-in fade-in slide-in-from-bottom-1"
                    style={{ animationDelay: `${(members?.length || 0) * 50 + index * 50}ms` }}
                  >
                    <InvitationCard
                      householdId={householdId}
                      invitation={invitation}
                      canCancel={isAdmin}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Owner warning about leaving */}
          {isOwner && members && members.length > 1 && (
            <p className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">
              <Trans>
                As the owner, you must transfer ownership to another member before you can leave
                this household.
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>

      <InviteMemberDialog
        householdId={householdId}
        householdName={householdName}
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
      />

      <LeaveHouseholdDialog
        householdId={householdId}
        householdName={householdName}
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
      />
    </>
  );
}
